import { ensureRouteData } from '@/app/query-client'
import { meQueryOptions, memberProfileQueryOptions } from '@/features/account/api/queries'
import type { RouterContext } from '@/routes/layouts/root'
import { parseSettingsTab, type SettingsTab } from '@/screens/account/components/account-tabs'

/**
 * `?tab=` → a validated tab, the route's own `validateSearch` and the shape
 * the loader below reads `location.search` through. One function for both,
 * as `stockSearchSchema` is for Stock, so the two can never disagree about
 * which tab a URL means.
 */
export const parseSettingsSearch = (search: Record<string, unknown>): { tab: SettingsTab } => ({
  tab: parseSettingsTab(search['tab']),
})

/**
 * The `/settings` loader.
 *
 * MAIN, keyed on `?tab=`, awaited only for the two tabs that read the member:
 * `edit-profile` draws from `me`/`profile`, and `security` from `profile`
 * alone (its Password row needs `hasPassword` and the email). The rest draw
 * from a browser-held setting (theme, language) or a static list of rows —
 * see `SettingsScreen.tsx`'s own doc — so their loader run is a no-op and the
 * route commits at once with no pending page.
 *
 * No `loaderDeps`: a tab switch is a search-only change on the SAME path and
 * re-runs no loader (`app/router.tsx`'s "Search params" rule), which is
 * exactly what keeps a chip click from a full-page skeleton. That means this
 * loader only ever runs for the tab a fresh navigation ARRIVES on — a direct
 * URL, a hard reload, or a link from another route (`ProfileScreen`'s
 * `SettingsRow`s, all of which change path too). A member who switches TO
 * `edit-profile` from another tab without leaving `/settings` gets no loader
 * run at all; `SettingsScreen`'s own `useMe`/`useMemberProfile` (plain
 * queries, not suspense — a suspense key that was never awaited would
 * suspend the whole route) fetch for themselves then, showing the exact
 * skeleton this loader is trying to avoid. Both paths land on the same two
 * cache keys either way.
 *
 * Its own module, as `app.guard.ts` is, so a test can mount it under a root
 * of its own — see the other loaders in this wave for the full reasoning.
 */
export async function loadSettings({
  context: { queryClient },
  location,
}: {
  context: RouterContext
  location: { search: unknown }
}): Promise<void> {
  const { tab } = parseSettingsSearch(location.search as Record<string, unknown>)
  if (tab === 'security') {
    await ensureRouteData(queryClient, memberProfileQueryOptions())
    return
  }
  if (tab !== 'edit-profile') return
  await Promise.all([
    ensureRouteData(queryClient, meQueryOptions()),
    ensureRouteData(queryClient, memberProfileQueryOptions()),
  ])
}
